import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, output, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { IconComponent } from '../../../../../../@core/components/ui/icon/icon.component';
import { DateRange, PeriodFilter, UntappedFeature, UntappedPotentialData, WorkspaceViewMode } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

interface FeatureVisual {
    iconName: string;
    iconBg: string;
    iconColor: string;
}

const FALLBACK_ICONS = ['zap', 'star', 'bookmark', 'flag', 'bell', 'shield', 'lock', 'globe', 'layers', 'grid', 'box', 'cpu', 'link', 'hash', 'filter', 'sliders', 'activity', 'bar-chart'];
const FALLBACK_COLORS = ['rgba(91,187,199,0.1)', 'rgba(255,118,42,0.1)', 'rgba(83,189,51,0.1)', 'rgba(229,159,47,0.1)', 'rgba(47,93,229,0.1)', 'rgba(220,177,22,0.1)', 'rgba(255,42,225,0.1)', 'rgba(80,163,206,0.1)'];
const FALLBACK_ICON_COLORS = ['#5bbbc7', '#ff762a', '#53bd33', '#e59f2f', '#2f5de5', '#dcb116', '#ff2ae1', '#50a3ce'];

function hashString(str: string): number {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
    return Math.abs(h);
}

@Component({
    selector: 'app-untapped-potential-widget',
    standalone: true,
    imports: [CommonModule, IconComponent, TooltipModule, SkeletonModule],
    templateUrl: './untapped-potential-widget.component.html',
    styleUrl: './untapped-potential-widget.component.scss',
})
export class UntappedPotentialWidgetComponent implements OnInit {
    readonly data = signal<UntappedPotentialData | null>(null);
    readonly loading = signal(false);

    readonly featureClick = output<UntappedFeature>();

    private readonly featureVisualMap: Record<string, FeatureVisual> = {
        'summary': { iconName: 'list', iconBg: 'rgba(91,187,199,0.1)', iconColor: '#5bbbc7' },
        'tags': { iconName: 'tags', iconBg: 'rgba(255,42,225,0.1)', iconColor: '#ff2ae1' },
        'checklist-items': { iconName: 'list-checks', iconBg: 'rgba(220,177,22,0.1)', iconColor: '#dcb116' },
        'compare': { iconName: 'git-compare', iconBg: 'rgba(255,118,42,0.1)', iconColor: '#ff762a' },
        'chat-with-ai': { iconName: 'bot', iconBg: 'rgba(80,163,206,0.1)', iconColor: '#50a3ce' },
        'generate-with-ai': { iconName: 'sparkles', iconBg: 'rgba(83,189,51,0.1)', iconColor: '#53bd33' },
        'contract-generator': { iconName: 'file-pen-line', iconBg: 'rgba(229,159,47,0.1)', iconColor: '#e59f2f' },
        'questionnaire': { iconName: 'message-circle-question', iconBg: '#d1fffd', iconColor: '#00bdb7' },
        'clause-library': { iconName: 'library-big', iconBg: '#d7f3ff', iconColor: '#0096c7' },
        'template-library': { iconName: 'layout-template', iconBg: 'rgba(47,93,229,0.1)', iconColor: '#2f5de5' },
    };

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            const mode = this.state.viewMode();
            const period = this.state.period();
            const range = this.state.effectiveRange();
            const workspaceId = this.state.workspaceId();
            this.fetch(mode, period, range, workspaceId);
        });
    }

    ngOnInit(): void { }

    getFeatureVisual(id: string): FeatureVisual {
        if (this.featureVisualMap[id]) return this.featureVisualMap[id];
        const h = hashString(id);
        return { iconName: FALLBACK_ICONS[h % FALLBACK_ICONS.length], iconBg: FALLBACK_COLORS[h % FALLBACK_COLORS.length], iconColor: FALLBACK_ICON_COLORS[h % FALLBACK_ICON_COLORS.length] };
    }

    onFeatureClick(feature: UntappedFeature): void {
        this.featureClick.emit(feature);
    }

    private fetch(
        mode: WorkspaceViewMode,
        period: PeriodFilter,
        range: DateRange | null,
        workspaceId: string,
    ): void {
        this.loading.set(true);
        this.service.getUntappedPotential({ mode, period, range, workspaceId }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: () => this.loading.set(false),
        });
    }
}
